import React from "react";
import {
  Warehouse,
  Truck,
  PackageSearch,
  BarChart3,
  ShieldCheck,
  Globe2,
  CheckCircle, Package,
  MoveRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from 'react-helmet-async';
import processImage from "../assets/images/data/3pl.png";
import MoreServicesSection from "./MoreServicesSection";
import CTA from "./CTA";

const ThreePLPage = () => (
  <main className="bg-white text-gray-800 overflow-hidden relative font-sans">
    <Helmet>
      <title>3PL Services | S INFINITE EXPRESS</title>
      <meta
        name="description"
        content="Scalable 3PL services by S INFINITE EXPRESS including warehousing, transportation, fulfillment, and real-time logistics technology."
      />
      <meta
        name="keywords"
        content="3PL services India, third party logistics company, warehousing and fulfillment, supply chain outsourcing, S Infinite Express 3PL, pan India logistics, ecommerce 3PL India, inventory management services, order fulfillment India"
      />
      <meta name="author" content="S Infinite Express" />
      <link rel="canonical" href="https://sinfiniteexpress.com/services/3pl" />
    </Helmet>

    {/* INTRO */}
    <section className="max-w-7xl mx-auto py-20 px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="space-y-6"
      >
        <h2 className="text-3xl md:text-4xl font-bold text-[#211f57]">
          Why Choose Our 3PL Services?
        </h2>
        <div className="w-20 h-1 bg-[#d32c30] rounded"></div>

        <p className="text-gray-700 text-lg">
          <span className="font-bold text-[#d32c30]">S INFINITE EXPRESS</span>{" "}
          delivers end-to-end Third-Party Logistics (3PL) solutions that seamlessly
          integrate warehousing, transportation, and fulfillment into one unified
          supply chain ecosystem. From inventory management and order processing to
          last-mile distribution and reverse logistics, we streamline every stage of
          your logistics journey.
        </p>

        <motion.img
          src={processImage}
          alt="3PL Logistics Process"
          className="rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-700 border-4 border-white"
          loading="lazy"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        />
      </motion.div>

      {/* 3PL Services */}
      <div className="bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0] text-black p-8 rounded-2xl shadow-xl space-y-6">

        {/* Heading */}
        <h3 className="text-xl font-bold flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-white/80 flex items-center justify-center shadow">
            <Package className="w-5 h-5 text-[#d32c30]" />
          </span>
          Logistics That Scale With You
        </h3>

        {/* Description */}
        <p className="leading-relaxed">
          Our 3PL solutions help you <strong>simplify operations</strong>,
          reduce overhead costs, and improve delivery performance.
          From warehousing to last-mile distribution, we provide
          <strong> end-to-end supply chain visibility</strong> powered by
          technology and dedicated operational support.
        </p>

        {/* Feature List */}
        <ul className="mt-10 grid sm:grid-cols-2 gap-4 text-sm text-gray-700">
          {[
            "Centralized inventory control across multiple warehouses",
            "Technology-driven order fulfillment & automation",
            "Nationwide warehousing & distribution network",
            "Dedicated account manager for personalized support",
            "Real-time inventory & shipment visibility dashboard",
            "Seamless returns & reverse logistics management",
            "Scalable infrastructure for seasonal demand spikes",
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-bold">
                ✓
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="bg-white/70 p-4 rounded-xl text-center shadow-sm">
            <p className="text-2xl font-extrabold text-[#d32c30]">99.5%</p>
            <p className="text-gray-800">Inventory Accuracy</p>
          </div>
          <div className="bg-white/70 p-4 rounded-xl text-center shadow-sm">
            <p className="text-2xl font-extrabold text-[#d32c30]">24–48h</p>
            <p className="text-gray-800">Order Fulfillment SLA</p>
          </div>
          <div className="bg-white/70 p-4 rounded-xl text-center shadow-sm">
            <p className="text-2xl font-extrabold text-[#d32c30]">Pan-India</p>
            <p className="text-gray-800">Warehouse Network</p>
          </div>
        </div>

        {/* CTA Button */}
        <a
          href="/get-a-quote"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#d32c30] text-white rounded-lg text-sm font-semibold hover:bg-[#b82629] transition shadow-md"
        >
          Get 3PL Quote
          <MoveRight className="w-4 h-4" />
        </a>
      </div>

    </section>

    {/* FEATURES */}
    <section className="py-20 bg-gradient-to-br from-[#211f57] to-[#2f2c72]">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-extrabold text-center text-[#fff44f] mb-4">
          Our 3PL Capabilities
        </h2>
        <p className="text-center text-white max-w-3xl mx-auto mb-12">
          Integrated logistics services designed to streamline your supply chain
          from storage to final delivery.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: <Warehouse className="w-6 h-6 text-blue-600" />,
              bg: "bg-blue-50",
              title: "Warehousing & Storage",
              description: "Secure, scalable warehouses with real-time inventory tracking.",
            },
            {
              icon: <PackageSearch className="w-6 h-6 text-green-600" />,
              bg: "bg-blue-100",
              title: "Order Fulfillment",
              description: "Accurate picking, packing, and fast dispatch.",
            },
            {
              icon: <Truck className="w-6 h-6 text-purple-600" />,
              bg: "bg-blue-50",
              title: "Transportation Management",
              description: "Optimized first-mile, mid-mile & last-mile delivery.",
            },
            {
              icon: <BarChart3 className="w-6 h-6 text-yellow-600" />,
              bg: "bg-blue-100",
              title: "Analytics & Reporting",
              description: "Performance dashboards and real-time logistics insights.",
            },
            {
              icon: <ShieldCheck className="w-6 h-6 text-red-600" />,
              bg: "bg-blue-50",
              title: "Secure Operations",
              description: "Process-driven, compliant, and damage-controlled handling.",
            },
            {
              icon: <Globe2 className="w-6 h-6 text-teal-600" />,
              bg: "bg-blue-100",
              title: "Pan-India Network",
              description: "Multi-location distribution across India.",
            },
            {
              icon: <CheckCircle className="w-6 h-6 text-[#211f57]" />,
              bg: "bg-blue-50",
              title: "Single Point of Control",
              description: "One partner for your complete supply chain.",
            },
            {
              icon: <Truck className="w-6 h-6 text-pink-600" />,
              bg: "bg-blue-100",
              title: "Door-to-Door Logistics",
              description: "End-to-end movement from origin to destination.",
            },
            {
              icon: <PackageSearch className="w-6 h-6 text-orange-600" />,
              bg: "bg-blue-50",
              title: "Ecommerce & B2B Ready",
              description: "Fulfillment designed for D2C, ecommerce & enterprises.",
            },
          ].map((feature, index) => (
            <div
              key={index}
              className={`group ${feature.bg} p-6 rounded-xl backdrop-blur-sm shadow-md 
              hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 
              hover:bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0]`}
            >
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center group-hover:bg-white transition">
                  {feature.icon}
                </div>
              </div>

              <h3 className="text-lg font-bold text-[#211f57] mb-2 group relative inline-block">
                {feature.title}
                <span className="absolute left-0 -bottom-1 h-0.5 w-0 bg-[#211f57] transition-all duration-300 group-hover:w-full"></span>
              </h3>

              <p className="text-sm text-gray-700">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <MoreServicesSection />
    <CTA />
  </main>
);

export default ThreePLPage;
